import {
  FunctionComponent,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import CookieConsentContext, {
  CookieConsentContextType,
  ONETRUST_CATEGORIES,
} from "@context/context/cookie-consent-context";

/**
 * Default consent groups for testing and development environments.
 *
 * @description All OneTrust cookie categories are enabled by default to ensure
 * comprehensive content rendering during testing and Storybook development.
 * In production, these values would be determined by actual user consent choices.
 *
 * @constant {string[]} defaultActiveGroups - Array of enabled cookie category IDs
 */
export const DEVELOPMENT_CONSENT_GROUPS = [
  ONETRUST_CATEGORIES.essential,
  ONETRUST_CATEGORIES.performance,
  ONETRUST_CATEGORIES.functional,
  ONETRUST_CATEGORIES.targeting,
  ONETRUST_CATEGORIES.socialMediaAndEmbeds,
];

export const DEVELOPMENT_CONSENT_CONTEXT_VALUE: CookieConsentContextType = {
  activeConsentGroups: DEVELOPMENT_CONSENT_GROUPS,
  isOneTrustLoading: false,
  enableSocialMediaCookies: () => {},
  toggleCookiesConsentDrawer: () => {},
};

declare global {
  interface Window {
    OnetrustActiveGroups?: string;
    OneTrust?: {
      UpdateConsent: (groupIdType: string, bitValue: string) => void;
      ToggleInfoDisplay: () => void;
    };
  }
}

interface Props {
  children?: ReactNode;
}

const getActiveConsentGroups = () => {
  if (typeof window !== "undefined" && window.OnetrustActiveGroups) {
    const consentGroups = window.OnetrustActiveGroups.split(",");
    const filteredConsentGroups = consentGroups.filter((group) => group !== "");
    return filteredConsentGroups;
  }

  return null;
};

const ConsentProvider: FunctionComponent<PropsWithChildren<Props>> = ({
  children,
}) => {
  const [activeConsentGroups, setActiveConsentGroups] = useState<string[]>([]);
  const [isOneTrustLoading, setIsOneTrustLoading] = useState<boolean>(true);

  const retryRef = useRef<NodeJS.Timeout | null>(null);

  const toggleCookiesConsentDrawer = useCallback(() => {
    if (typeof window !== "undefined" && window.OneTrust) {
      window.OneTrust.ToggleInfoDisplay();
    }
  }, []);

  const enableSocialMediaCookies = useCallback(() => {
    if (typeof window !== "undefined" && window.OneTrust) {
      const bitValue = `${ONETRUST_CATEGORIES.socialMediaAndEmbeds}:1`;
      window.OneTrust.UpdateConsent("Category", bitValue);
    }
  }, []);

  /**
   * Check for initial consent groups on mount
   */
  useEffect(() => {
    const checkOneTrustStatus = () => {
      const newActiveConsentGroups = getActiveConsentGroups();
      if (newActiveConsentGroups) {
        setActiveConsentGroups(newActiveConsentGroups);
        setIsOneTrustLoading(false);
      } else {
        // If OneTrust isn't loaded yet, set a timeout to check again
        if (retryRef.current) {
          clearTimeout(retryRef.current);
        }

        retryRef.current = setTimeout(checkOneTrustStatus, 50);
      }
    };

    checkOneTrustStatus();
    return () => {
      if (retryRef.current) {
        clearTimeout(retryRef.current);
      }
    };
  }, []);

  /**
   * Listen for OneTrust consent group updates
   */
  useEffect(() => {
    const handleConsentGroups = (event: Event) => {
      const customEvent = event as CustomEvent<string[]>;
      setActiveConsentGroups(customEvent.detail);
    };

    window.addEventListener("OneTrustGroupsUpdated", handleConsentGroups);
    return () => {
      window.removeEventListener("OneTrustGroupsUpdated", handleConsentGroups);
    };
  }, []);

  const contextValue: CookieConsentContextType = {
    activeConsentGroups,
    isOneTrustLoading,
    enableSocialMediaCookies,
    toggleCookiesConsentDrawer,
  };

  return (
    <CookieConsentContext.Provider
      value={
        process.env.NODE_ENV === "development"
          ? DEVELOPMENT_CONSENT_CONTEXT_VALUE
          : contextValue
      }
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export default ConsentProvider;
